<template>
  <div class="answer-page">
    <div class="answer-container">
      <!-- 答题头部设置 -->
      <div class="answer-header">
        <h1>分类答题</h1>
        <div class="answer-info">
          <div class="info-item">
            <span class="label">当前组别：</span>
            <select class="form-control" v-model="selectedGroup">
              <option value="primary">小学组</option>
              <option value="middle">初中组</option>
              <option value="high">高中组</option>
            </select>
          </div>
          <div class="info-item">
            <span class="label">题型：</span>
            <select class="form-control" v-model="selectedType">
              <option value="all">全部题型</option>
              <option value="single">单选题</option>
              <option value="multiple">多选题</option>
            </select>
          </div>
          <div class="info-item">
            <span class="label">题目数量：</span>
            <select class="form-control" v-model="questionCount">
              <option value="5">5题</option>
              <option value="10">10题</option>
              <option value="15">15题</option>
              <option value="20">20题</option>
            </select>
          </div>
          <button class="btn btn-primary start-btn" @click="startQuiz">开始答题</button>
        </div>
      </div>

      <!-- 开始答题提示 -->
      <div class="start-section" v-if="!isAnswering && !loading">
        <div class="start-card">
          <div class="start-icon">📝</div>
          <h2>准备开始答题</h2>
          <p>请选择组别、题型和题目数量，然后点击「开始答题」按钮</p>
          <div class="start-info">
            <div class="info-item">
              <span class="label">小学组：</span>
              <span class="desc">基础无人机知识，适合初学者</span>
            </div>
            <div class="info-item">
              <span class="label">初中组：</span>
              <span class="desc">进阶无人机原理，适合有一定基础的学习者</span>
            </div>
            <div class="info-item">
              <span class="label">高中组：</span>
              <span class="desc">高级无人机技术，适合深入学习的学习者</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 加载状态 -->
      <div class="loading-section" v-if="loading">
        <div class="loading-card">
          <div class="loading-icon">⏳</div>
          <h2>正在加载题目...</h2>
        </div>
      </div>

      <!-- 答题主体 -->
      <div class="answer-body" v-if="isAnswering">
        <div class="question-area">
          <div class="question-nav">
            <div class="question-number">第 <span>{{ currentIndex + 1 }}</span> 题</div>
          </div>

          <div class="question-content">
            <div class="current-question">
              <div class="question-type">
                <span class="badge badge-primary">{{ currentQuestion.type === 'single' ? '单选题' : '多选题' }}</span>
                <span class="badge badge-warning">{{ getGroupLabel(currentQuestion.group) }}</span>
              </div>
              <h2 class="question-title">{{ currentQuestion.title }}</h2>
              
              <!-- 题目图片 -->
              <div v-if="currentQuestion.titleImages && currentQuestion.titleImages.length > 0" class="question-images">
                <img 
                  v-for="(image, imgIndex) in currentQuestion.titleImages" 
                  :key="image.id || imgIndex"
                  :src="image.imageUrl"
                  :alt="`题目图片 ${imgIndex + 1}`"
                  class="question-image"
                  @click="openImagePreview(image.imageUrl)"
                  style="cursor: pointer;"
                />
              </div>
              
              <div class="options-list">
                <div 
                  v-for="(option, optIndex) in currentQuestion.options" 
                  :key="optIndex"
                  class="option-item"
                  :class="{ 
                    selected: isOptionSelected(option.key),
                    correct: showExplanation && currentQuestion.answer.includes(option.key),
                    wrong: showExplanation && isOptionSelected(option.key) && !currentQuestion.answer.includes(option.key)
                  }"
                  @click="selectOption(option.key)"
                >
                  <span class="option-label">{{ option.key }}</span>
                  <span class="option-text">{{ option.text }}</span>
                  <!-- 选项图片 -->
                  <div v-if="option.optionImages && option.optionImages.length > 0" class="option-images">
                    <img 
                      v-for="(img, imgIndex) in option.optionImages" 
                      :key="img.id || imgIndex"
                      :src="img.imageUrl"
                      :alt="`选项图片 ${imgIndex + 1}`"
                      class="option-image"
                      @click="openImagePreview(img.imageUrl)"
                      style="cursor: pointer;"
                    />
                  </div>
                </div>
              </div>

              <div class="answer-actions">
                <button 
                  class="nav-btn prev-btn" 
                  :disabled="currentIndex === 0"
                  @click="prevQuestion"
                >
                  上一题
                </button>
                <button 
                  class="btn btn-primary" 
                  @click="confirmSubmit"
                >提交答案</button>
                <button 
                  class="nav-btn next-btn" 
                  :disabled="currentIndex === questions.length - 1 || !showExplanation"
                  @click="nextQuestion"
                >
                  下一题
                </button>
              </div>
            </div>
          </div>

          <!-- 解析区域 -->
          <div class="explanation-section" v-if="showExplanation">
            <div class="explanation-header">
              <span class="answer-result" :class="{ correct: isCurrentAnswerCorrect }">
                {{ isCurrentAnswerCorrect ? '✓ 回答正确' : '✗ 回答错误' }}
              </span>
            </div>
            <div class="explanation-content">
              <h4>正确答案：</h4>
              <p>{{ currentQuestion.answer.join('') }}</p>
              <h4>解析：</h4>
              <p>{{ currentQuestion.explanation || '暂无解析' }}</p>
            </div>
          </div>
        </div>

      </div>

      <!-- 结果页面 -->
      <div class="result-section" v-if="showResult">
        <div class="result-card">
          <div class="result-icon">🎉</div>
          <h2>答题完成</h2>
          <div class="result-stats">
            <div class="stat-item">
              <span class="stat-value correct">{{ correctCount }}</span>
              <span class="stat-label">正确题数</span>
            </div>
            <div class="stat-item">
              <span class="stat-value">{{ score }}</span>
              <span class="stat-label">得分</span>
            </div>
            <div class="stat-item">
              <span class="stat-value">{{ scorePercent }}%</span>
              <span class="stat-label">正确率</span>
            </div>
          </div>
          <div class="result-actions">
            <button class="btn btn-secondary" @click="reviewAnswers">查看答案</button>
            <button class="btn btn-primary" @click="restartQuiz">重新答题</button>
          </div>
        </div>
      </div>
    </div>

    <!-- 确认提交弹窗 -->
    <div class="modal" v-if="showConfirmModal">
      <div class="modal-content">
        <h2>确认提交</h2>
        <p>您还有 {{ unansweredCount }} 道题未完成，确定要提交吗？</p>
        <div class="modal-actions">
          <button class="btn btn-secondary" @click="showConfirmModal = false">继续答题</button>
          <button class="btn btn-primary" @click="submitQuiz">确认提交</button>
        </div>
      </div>
    </div>

    <!-- 提示弹窗 -->
    <div class="modal" v-if="showAlertModal">
      <div class="modal-content">
        <p class="alert-message">{{ alertMessage }}</p>
        <div class="modal-actions">
          <button class="btn btn-primary" @click="showAlertModal = false">确定</button>
        </div>
      </div>
    </div>

    <!-- 防切屏警告 -->
    <div class="anti-screen-warning" v-if="screenWarning">
      <div class="warning-content">
        <div class="warning-icon">⚠️</div>
        <h2>检测到切屏行为</h2>
        <p>为保障考试公平，请勿切换屏幕</p>
        <p>警告次数：<span>{{ warningCount }}</span>/3</p>
      </div>
    </div>
    
    <!-- 图片预览模态框 -->
    <div v-if="showImagePreview" class="image-preview-modal" @click="closeImagePreview">
      <div class="image-preview-content" @click.stop>
        <button class="close-btn" @click="closeImagePreview">&times;</button>
        <img :src="previewImageUrl" alt="预览图片" class="preview-image" />
      </div>
    </div>
  </div>
</template>

<script>
import { getRandomQuestions, addUselearn } from '@/api/droneApi';
import { baseURL } from '@/api/axiosConfig';
import { useAuthStore } from "@/store/authStore";

export default {
  name: 'AnswerPage',
  data() {
    return {
      selectedGroup: 'primary',
      selectedType: 'all',
      questionCount: '10',
      isAnswering: false,
      showResult: false,
      showConfirmModal: false,
      showExplanation: false,
      showAlertModal: false,
      alertMessage: '',
      currentIndex: 0,
      warningCount: 0,
      screenWarning: false,
      warningTimer: null,
      answers: {},
      questions: [],
      loading: false,
      showImagePreview: false,
      previewImageUrl: ''
    }
  },
  computed: {
    currentQuestion() {
      if (this.questions.length === 0) return {}
      const q = this.questions[this.currentIndex]
      if (q.marked === undefined) {
        q.marked = false
      }
      return q
    },
    answeredCount() {
      return Object.keys(this.answers).length
    },
    unansweredCount() {
      return this.questions.length - this.answeredCount
    },
    markedCount() {
      return this.questions.filter(q => q.marked).length
    },
    progressPercent() {
      if (this.questions.length === 0) return 0
      return Math.round((this.answeredCount / this.questions.length) * 100)
    },
    correctCount() {
      let count = 0
      this.questions.forEach((q, i) => {
        const userAnswer = this.answers[i] || []
        const correct = q.answer
        if (JSON.stringify(userAnswer.sort()) === JSON.stringify(correct.sort())) {
          count++
        }
      })
      return count
    },
    score() {
      return Math.round((this.correctCount / this.questions.length) * 100)
    },
    scorePercent() {
      return this.score
    },
    isCurrentAnswerCorrect() {
      if (!this.currentQuestion.answer) return false
      const userAnswer = this.answers[this.currentIndex] || []
      const correct = this.currentQuestion.answer
      return JSON.stringify(userAnswer.sort()) === JSON.stringify(correct.sort())
    }
  },
  methods: {
    getGroupLabel(group) {
      const labels = { primary: '小学', middle: '初中', high: '高中' }
      return labels[group] || '小学'
    },
    shuffleArray(array) {
      const newArray = [...array]
      for (let i = newArray.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
        ;[newArray[i], newArray[j]] = [newArray[j], newArray[i]]
      }
      return newArray
    },
    async startQuiz() {
      this.loading = true
      try {
        const params = {
          difficulty: this.selectedGroup === 'primary' ? 1 : this.selectedGroup === 'middle' ? 2 : 3,
          count: parseInt(this.questionCount),
          questionType:this.selectedType === 'all' ? '' //全部题型时不传或传空 
          :this.selectedType === 'single' ? '0' : '1'
        }
        
        const res = await getRandomQuestions(params)
        
        console.log('随机题目接口返回数据:', res)
        console.log('data字段:', res.data)
        console.log('data.data字段:', res.data.data)
        
        if (res.data.code === 200 && res.data.data) {
          // 检查是否有list字段
          const listData = Array.isArray(res.data.data.list) ? res.data.data.list : Array.isArray(res.data.data) ? res.data.data : []
          console.log('处理前的list数据:', listData)
          console.log('list长度:', listData.length)
          
          this.questions = listData.map(item => ({
            id: item.id,
            //关键修改，直接使用后端返回的questionType映射前端题型
            type: item.questionType === '0' ? 'single' : 'multiple',
            group: this.selectedGroup,
            title: item.questionTitle || '暂无题目内容',
            titleImages: item.titleImages ? item.titleImages.map(img => ({
              ...img,
              imageUrl: baseURL + img.imageUrl
            })) : [],
            options: item.options ? item.options.map(opt => ({
              id: opt.id || '',
              key: opt.optionLabel || '',
              text: opt.optionContent || '',
              isCorrect: opt.isCorrect === 1,
              optionImages: opt.optionImages ? opt.optionImages.map(img => ({
                ...img,
                imageUrl: baseURL + img.imageUrl
              })) : []
            })) : [],
            answer: item.options ? item.options
              .filter(opt => opt.isCorrect === 1)
              .map(opt => opt.optionLabel) : [],
            explanation: item.analysis || '暂无解析',
            marked: false
          }))
          
          this.answers = {}
          this.currentIndex = 0
          this.isAnswering = true
          this.showResult = false
          this.showExplanation = false
          
          // 监听切屏
          document.addEventListener('visibilitychange', this.handleVisibilityChange)
        } else {
          this.alertMessage = '获取题目失败，请稍后重试'
          this.showAlertModal = true
        }
      } catch (error) {
        console.error('获取题目失败:', error)
        this.alertMessage = '获取题目失败，请稍后重试'
        this.showAlertModal = true
      } finally {
        this.loading = false
      }
    },
    handleVisibilityChange() {
      if (document.hidden && this.isAnswering) {
        this.warningCount++
        this.screenWarning = true
        
        clearTimeout(this.warningTimer)
        this.warningTimer = setTimeout(() => {
          this.screenWarning = false
        }, 3000)
      }
    },
    isOptionSelected(key) {
      const answer = this.answers[this.currentIndex] || []
      return answer.includes(key)
    },
    selectOption(key) {
      if (!this.answers[this.currentIndex]) {
        this.answers[this.currentIndex] = []
      }
      
      const answer = this.answers[this.currentIndex]
      const index = answer.indexOf(key)
      
      if (this.currentQuestion.type === 'single') {
        // 单选题只选一个
        this.answers[this.currentIndex] = [key]
      } else {
        // 多选题切换选择
        if (index > -1) {
          answer.splice(index, 1)
        } else {
          answer.push(key)
        }
        this.answers[this.currentIndex] = [...answer]
      }
    },
    toggleMark() {
      const q = this.questions[this.currentIndex]
      if (q.marked) {
        q.marked = false
      } else {
        q.marked = true
      }
    },
    prevQuestion() {
      if (this.currentIndex > 0) {
        this.currentIndex--
      }
    },
    nextQuestion() {
      if (this.currentIndex < this.questions.length - 1) {
        this.currentIndex++
        this.showExplanation = false
      }
    },
    async confirmSubmit() {
      // 检查是否选择了答案
      const currentAnswer = this.answers[this.currentIndex]
      if (!currentAnswer || currentAnswer.length === 0) {
        this.alertMessage = '请选择作答'
        this.showAlertModal = true
        return
      }
      
      // 显示解析
      this.showExplanation = true
      
      // 存储错题记录（单题提交时存储）
      const authStore = useAuthStore();
      if (authStore.isAuthenticated) {
        const currentQuestion = this.questions[this.currentIndex];
        const correctAnswer = currentQuestion.answer;
        
        // 比较答案
        if (JSON.stringify(currentAnswer.sort()) !== JSON.stringify(correctAnswer.sort())) {
          try {
            // 获取用户选择的选项的 ID
            const userOptionIds = [];
            currentQuestion.options.forEach(option => {
              if (currentAnswer.includes(option.key)) {
                userOptionIds.push(option.id);
              }
            });
            
            // 构建错题记录数据（后端通过token解析用户ID）
            const wrongAnswerData = {
              questionbankId: currentQuestion.id,
              useroptionId: userOptionIds.join(',')
            };
            
            console.log('准备存储错题记录:', wrongAnswerData);
            // 调用接口存储错题
            const res = await addUselearn(wrongAnswerData);
            console.log('错题记录存储成功:', currentQuestion.id, res);
          } catch (error) {
            console.error('存储错题记录失败:', error);
          }
        }
      }
    },
    submitQuiz() {
      this.showConfirmModal = false
      this.isAnswering = false
      this.showResult = true
      document.removeEventListener('visibilitychange', this.handleVisibilityChange)
    },
    reviewAnswers() {
      console.log('查看答案', this.answers)
    },
    restartQuiz() {
      this.isAnswering = false
      this.showResult = false
      this.showExplanation = false
      this.answers = {}
      this.currentIndex = 0
      this.questions = []
    },
    openImagePreview(imageUrl) {
      this.previewImageUrl = imageUrl
      this.showImagePreview = true
    },
    closeImagePreview() {
      this.showImagePreview = false
      this.previewImageUrl = ''
    }
  },
  beforeDestroy() {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
    if (this.warningTimer) {
      clearTimeout(this.warningTimer)
    }
  }
}
</script>

<style scoped>
.answer-page {
  padding: 20px;
  min-height: calc(100vh - 200px);
  background: #f5f7fa;
}

.answer-container {
  max-width: 1100px;
  margin: 0 auto;
}

/* 头部设置 */
.answer-header {
  background: white;
  border-radius: 12px;
  padding: 20px 25px;
  margin-bottom: 25px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}

.answer-header h1 {
  font-size: 24px;
  color: #1a5f7a;
  margin-bottom: 20px;
  font-weight: bold;
}

.answer-info {
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.info-item .label {
  color: #666;
  font-size: 14px;
}

.form-control {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  min-width: 120px;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary {
  background: #1a5f7a;
  color: white;
}

.btn-primary:hover {
  background: #147a8a;
}

.btn-secondary {
  background: #e0e0e0;
  color: #333;
}

.btn-secondary:hover {
  background: #d0d0d0;
}

.btn-success {
  background: #27ae60;
  color: white;
}

.btn-success:hover {
  background: #219a52;
}

.start-btn {
  margin-left: auto;
}

/* 开始答题提示 */
.start-section {
  display: flex;
  justify-content: center;
  padding: 10px 0;
}

.start-card {
  background: white;
  border-radius: 16px;
  padding: 30px;
  text-align: center;
  max-width: 700px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.start-icon {
  font-size: 80px;
  margin-bottom: 10px;
}

.start-card h2 {
  color: #1a5f7a;
  margin-bottom: 15px;
}

.start-card p {
  color: #666;
  margin-bottom: 30px;
}

.start-info {
  display: flex;
  flex-direction: column;
  gap: 15px;
  text-align: left;
}

.start-info .info-item {
  padding: 12px 16px;
  background: #f8f9fa;
  border-radius: 8px;
}

.start-info .label {
  font-weight: bold;
  color: #1a5f7a;
}

.start-info .desc {
  color: #666;
}

/* 加载状态 */
.loading-section {
  display: flex;
  justify-content: center;
  padding: 60px 0;
}

.loading-card {
  background: white;
  border-radius: 16px;
  padding: 50px;
  text-align: center;
  max-width: 400px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.loading-icon {
  font-size: 80px;
  margin-bottom: 20px;
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

.loading-card h2 {
  color: #1a5f7a;
  margin-bottom: 15px;
}

/* 答题主体 */
.answer-body {
  display: flex;
  gap: 25px;
  flex-direction: column;
}

.question-area {
  flex: 1;
  background: white;
  border-radius: 12px;
  padding: 25px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  max-width: 1100px;
  margin: 0 auto;
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
}

.question-nav {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 25px;
  padding-bottom: 15px;
  border-bottom: 1px solid #eee;
  width: 100%;
}

.nav-btn {
  padding: 8px 20px;
  border: 1px solid #ddd;
  background: white;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.nav-btn:hover:not(:disabled) {
  background: #1a5f7a;
  border-color: #1a5f7a;
  color: white;
}

.nav-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.question-number {
  font-size: 16px;
  color: #666;
}

.question-number span {
  font-weight: bold;
  color: #1a5f7a;
}

.question-content {
  flex: 1;
  min-width: 500px;
  max-width: 550px;
}

.current-question {
  min-height: 300px;
}

.question-type {
  display: flex;
  gap: 10px;
  margin-bottom: 15px;
}

.badge {
  padding: 5px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.badge-primary {
  background: #e8f4f8;
  color: #1a5f7a;
}

.badge-warning {
  background: #fff3cd;
  color: #856404;
}

.question-title {
  font-size: 18px;
  color: #333;
  margin-bottom: 25px;
  line-height: 1.6;
}

/* 题目图片 */
.question-images {
  margin: 20px 0;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.question-image {
  max-width: 100%;
  max-height: 200px;
  border-radius: 6px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.options-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.option-item {
  display: flex;
  align-items: center;
  padding: 15px 20px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.option-item:hover {
  border-color: #1a5f7a;
  background: #f8f9fa;
}

.option-item.selected {
  border-color: #1a5f7a;
  background: #e8f4f8;
}

.option-label {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0f0f0;
  border-radius: 50%;
  font-weight: bold;
  margin-right: 15px;
  flex-shrink: 0;
}

.option-item.selected .option-label {
  background: #1a5f7a;
  color: white;
}

.option-text {
  font-size: 15px;
  color: #333;
}

/* 选项图片 */
.option-images {
  margin-top: 10px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.option-image {
  max-width: 100px;
  max-height: 100px;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.option-item.correct {
  border-color: #27ae60 !important;
  background: #e8f9ed !important;
}

.option-item.wrong {
  border-color: #e74c3c !important;
  background: #fdeaec !important;
}

.explanation-section {
  flex: 1;
  min-width: 500px;
  max-width: 550px;
  padding: 20px;
  background: #fffbf0;
  border-radius: 8px;
  border: 1px solid #fff3cd;
}

.explanation-header {
  margin-bottom: 15px;
}

.answer-result {
  font-size: 16px;
  font-weight: bold;
  padding: 6px 15px;
  border-radius: 20px;
  background: #e74c3c;
  color: white;
}

.answer-result.correct {
  background: #27ae60;
}

.explanation-content h4 {
  font-size: 14px;
  color: #856404;
  margin: 15px 0 8px;
}

.explanation-content p {
  font-size: 14px;
  color: #666;
  line-height: 1.7;
  margin: 0;
}

.answer-actions {
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #eee;
}

/* 结果页面 */
.result-section {
  display: flex;
  justify-content: center;
  padding: 50px 0;
}

.result-card {
  background: white;
  border-radius: 16px;
  padding: 50px;
  text-align: center;
  max-width: 500px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.result-icon {
  font-size: 80px;
  margin-bottom: 20px;
}

.result-card h2 {
  color: #1a5f7a;
  margin-bottom: 30px;
}

.result-stats {
  display: flex;
  justify-content: center;
  gap: 40px;
  margin-bottom: 30px;
}

.stat-item {
  text-align: center;
}

.stat-item .stat-value {
  display: block;
  font-size: 36px;
  font-weight: bold;
  color: #1a5f7a;
  margin-bottom: 8px;
}

.stat-item .stat-value.correct {
  color: #27ae60;
}

.stat-item .stat-label {
  font-size: 14px;
  color: #666;
}

.result-actions {
  display: flex;
  justify-content: center;
  gap: 15px;
}

/* 模态框 */
.modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 16px;
  padding: 30px;
  text-align: center;
  max-width: 400px;
}

.modal-content h2 {
  margin-bottom: 15px;
  color: #333;
}

.modal-content p {
  color: #666;
  margin-bottom: 10px;
}

.modal-actions {
  display: flex;
  gap: 15px;
  justify-content: center;
  margin-top: 25px;
}

.alert-message {
  font-size: 18px;
  color: #333;
  text-align: center;
  padding: 20px 0;
}

/* 防切屏警告 */
.anti-screen-warning {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.warning-content {
  text-align: center;
  color: white;
}

.warning-icon {
  font-size: 80px;
  margin-bottom: 20px;
}

.warning-content h2 {
  color: #e74c3c;
  margin-bottom: 15px;
}

.warning-content p {
  margin-bottom: 10px;
  font-size: 18px;
}

.warning-content span {
  color: #e74c3c;
  font-weight: bold;
  font-size: 24px;
}

@media (max-width: 900px) {
  .answer-body {
    flex-direction: column;
  }
  
  .answer-sidebar {
    width: 100%;
  }
  
  .question-content {
    min-width: 100%;
    max-width: 100%;
  }
  
  .explanation-section {
    min-width: 100%;
    max-width: 100%;
  }
  
  .question-area {
    padding: 20px;
  }
}

@media (max-width: 768px) {
  .answer-page {
    padding: 10px;
    min-height: calc(100vh - 180px);
  }
  
  .answer-container {
    max-width: 100%;
  }
  
  .answer-header {
    padding: 15px;
  }
  
  .answer-header h1 {
    font-size: 20px;
  }
  
  .answer-info {
    gap: 10px;
  }
  
  .info-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
  
  .form-control {
    min-width: 100px;
    padding: 6px 10px;
  }
  
  .btn {
    padding: 8px 16px;
    font-size: 13px;
  }
  
  .start-btn {
    margin-left: 0;
    width: 100%;
  }
  
  .start-card {
    padding: 20px;
    max-width: 100%;
  }
  
  .start-icon {
    font-size: 60px;
  }
  
  .question-area {
    padding: 15px;
  }
  
  .question-title {
    font-size: 16px;
    margin-bottom: 20px;
  }
  
  .option-item {
    padding: 12px 16px;
  }
  
  .option-label {
    width: 28px;
    height: 28px;
    margin-right: 12px;
  }
  
  .option-text {
    font-size: 14px;
  }
  
  .answer-actions {
    gap: 10px;
    margin-top: 20px;
    padding-top: 15px;
  }
  
  .nav-btn {
    padding: 6px 16px;
  }
  
  .explanation-section {
    padding: 15px;
  }
  
  .explanation-content h4 {
    font-size: 13px;
  }
  
  .explanation-content p {
    font-size: 13px;
  }
  
  .result-card {
    padding: 30px;
    max-width: 100%;
  }
  
  .result-icon {
    font-size: 60px;
  }
  
  .result-stats {
    gap: 20px;
  }
  
  .stat-item .stat-value {
    font-size: 28px;
  }
  
  .modal-content {
    padding: 20px;
    max-width: 90%;
  }
  
  .question-image {
    max-height: 150px;
  }
  
  .option-image {
    max-width: 80px;
    max-height: 80px;
  }
}

@media (min-width: 769px) and (max-width: 1024px) {
  .answer-page {
    padding: 15px;
  }
  
  .answer-container {
    max-width: 900px;
  }
  
  .question-content {
    min-width: 100%;
    max-width: 100%;
  }
  
  .explanation-section {
    min-width: 100%;
    max-width: 100%;
  }
  
  .question-area {
    padding: 20px;
  }
}

/* 图片预览模态框 */
.image-preview-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.image-preview-content {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn {
  position: absolute;
  top: -40px;
  right: 0;
  width: 40px;
  height: 40px;
  background: white;
  border: none;
  border-radius: 50%;
  font-size: 30px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #333;
  transition: all 0.2s;
}

.close-btn:hover {
  background: #f0f0f0;
}

.preview-image {
  max-width: 100%;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 8px;
}
</style>
